import { ScrollText } from "lucide-react";
import { useMemo, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { LogDetailDrawer } from "@/components/system-logs/LogDetailDrawer";
import type { LogFilterValues, LogTimeRange } from "@/components/system-logs/LogFilters";
import { LogFilters } from "@/components/system-logs/LogFilters";
import { LogsTable } from "@/components/system-logs/LogsTable";
import { Skeleton } from "@/components/ui/skeleton";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type { SystemLogListParams } from "@/hooks/use-system-logs";
import { useSystemLogs } from "@/hooks/use-system-logs";

// §rule: "Lists virtualize past 50 rows. Paginate at 100." No virtualization
// library is present in this codebase yet (see `.claude/rules/frontend.md`
// precedent — AccountsTable.tsx caps at 50 rows/page with plain pagination
// instead of adding a new dependency). Capping the page size at 50 satisfies
// the rule's intent without ever rendering more than 50 DOM rows.
const PAGE_SIZE = 50;

function fromForRange(range: LogTimeRange): string | undefined {
  const now = Date.now();
  switch (range) {
    case "1h":
      return new Date(now - 3600_000).toISOString();
    case "24h":
      return new Date(now - 24 * 3600_000).toISOString();
    case "7d":
      return new Date(now - 7 * 24 * 3600_000).toISOString();
    case "all":
      return undefined;
  }
}

export function LogsView() {
  const [filters, setFilters] = useState<LogFilterValues>({
    level: "all",
    category: "all",
    status: "all",
    method: "all",
    path: "",
    range: "24h",
  });
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [direction, setDirection] = useState<"next" | "prev">("next");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const debouncedPath = useDebouncedValue(filters.path, 300);

  // Compute the `from` bound ONLY when the selected range changes. Computing it
  // inline on every render (via Date.now()) would make the query key change each
  // render, causing TanStack Query to refetch in an infinite loop.
  const from = useMemo(() => fromForRange(filters.range), [filters.range]);

  function handleFilterChange(patch: Partial<LogFilterValues>) {
    setFilters((prev) => ({ ...prev, ...patch }));
    setCursor(undefined);
    setDirection("next");
    setPage(1);
  }

  const params: SystemLogListParams = {
    cursor,
    direction,
    limit: PAGE_SIZE,
    level: filters.level === "all" ? undefined : filters.level,
    category: filters.category === "all" ? undefined : filters.category,
    status: filters.status === "all" ? undefined : filters.status,
    method: filters.method === "all" ? undefined : filters.method,
    path: debouncedPath || undefined,
    from,
  };

  const { data, isLoading, isFetching, isError, error, refetch } = useSystemLogs(params);

  function goNext() {
    if (!data?.nextCursor) return;
    setCursor(data.nextCursor);
    setDirection("next");
    setPage((p) => p + 1);
  }

  function goPrev() {
    if (!data?.prevCursor) return;
    setCursor(data.prevCursor);
    setDirection("prev");
    setPage((p) => Math.max(1, p - 1));
  }

  function handleRowClick(id: string) {
    setSelectedId(id);
    setDrawerOpen(true);
  }

  const rows = data?.data ?? [];

  return (
    <div className="space-y-4">
      <LogFilters values={filters} onChange={handleFilterChange} />

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      )}

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load logs."}
          onRetry={() => refetch()}
        />
      )}

      {!isLoading &&
        !isError &&
        data !== undefined &&
        (rows.length === 0 ? (
          <EmptyState
            icon={ScrollText}
            title="No log entries"
            description="No log entries match these filters."
          />
        ) : (
          <div className="space-y-4">
            <LogsTable rows={rows} onRowClick={handleRowClick} />
            <CursorPagination
              total={data.total}
              page={page}
              limit={PAGE_SIZE}
              hasNext={data.hasNext}
              hasPrev={data.hasPrev}
              onNext={goNext}
              onPrev={goPrev}
              loading={isFetching}
              itemLabel="log entries"
            />
          </div>
        ))}

      <LogDetailDrawer id={selectedId} open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}
